import { GridApi } from 'ag-grid-community';

/**
 * The members of ag-Grid 29's AnimationFrameService that `guardGridFrameLoop` relies on. `executeFrame` and
 * `requestFrame` are private in ag-Grid's typings, hence this local shape. `GridApi` keeps the service in its private
 * `animationFrameService` field, the one behind the public `flushAllAnimationFrames()`.
 */
interface GridFrameLoop {
    executeFrame(millis: number): void;
    requestFrame(): void;
    isAlive(): boolean;
    vfFrameGuard?: true;
}

/**
 * How many frames in a row may fail before the guard stops asking for another. A failing task is taken off the queue
 * before it runs, so a real backlog drains one failure per frame. This only stops a frame that fails before reaching
 * any task from asking for itself forever.
 */
const MAX_FAILED_FRAMES_IN_A_ROW = 100;

/**
 * Keeps one failed frame task from freezing a grid for the rest of its life.
 *
 * ag-Grid 29.3.5 does its deferred work in an animation-frame queue. That includes creating the cell renderers of rows
 * it has just drawn, laying cells out after a scroll, and applying a chevron expand/collapse: `rowNode.setExpanded` is
 * debounced onto that queue. `AnimationFrameService.executeFrame` runs the tasks with no try/finally, and it clears its
 * `ticking` flag only when a frame ends normally. If ANY task throws, `ticking` stays true and no frame is ever asked
 * for again. After that a section's chevron still flips its icon, but the rows under it never hide. Collapse all and
 * Expand all keep working, because they refresh synchronously. Only a new grid instance clears it.
 *
 * This wraps the grid's `executeFrame`. When a task throws, the guard asks for the next frame so the rest of the queue
 * still runs. The failed task was already taken off the queue. The error is then thrown again on its own tick, so it
 * is still reported like any uncaught error.
 *
 * @returns false, changing nothing, when the grid is already guarded or the internals are not where ag-Grid 29 keeps
 * them (a later ag-Grid).
 */
export function guardGridFrameLoop(api: GridApi | null | undefined): boolean {
    const loop: GridFrameLoop | undefined = (api as unknown as { animationFrameService?: GridFrameLoop } | null)
        ?.animationFrameService;
    if (
        !loop ||
        loop.vfFrameGuard ||
        typeof loop.executeFrame !== 'function' ||
        typeof loop.requestFrame !== 'function' ||
        typeof loop.isAlive !== 'function'
    ) {
        return false;
    }
    const executeFrame: (millis: number) => void = loop.executeFrame.bind(loop);
    let failedFramesInARow = 0;
    loop.executeFrame = (millis: number): void => {
        try {
            executeFrame(millis);
            failedFramesInARow = 0;
        } catch (error) {
            failedFramesInARow++;
            if (loop.isAlive() && failedFramesInARow < MAX_FAILED_FRAMES_IN_A_ROW) {
                loop.requestFrame();
            }
            setTimeout(() => {
                throw error;
            });
        }
    };
    loop.vfFrameGuard = true;
    return true;
}

/**
 * The members of an ag-Grid 29 RowCtrl (the controller of one row) that `guardGridRedraw` reads. `leftGui` and
 * `rightGui` are set while the row has an element in the pinned-left / pinned-right section.
 */
interface GridRowCtrl {
    getRowNode(): { displayed: boolean };
    leftGui?: object;
    rightGui?: object;
}

/** A row section's controller (ag-Grid 29 RowContainerCtrl). `getRowCtrls` lists the rows the section should draw. */
interface GridRowContainer {
    getRowCtrls(): GridRowCtrl[];
}

/** The part of ag-Grid 29's CtrlsService that hands out the row sections. `GridApi` keeps it in `ctrlsService`. */
interface GridCtrls {
    whenReady(
        callback: (ctrls: { leftRowContainerCtrl?: GridRowContainer; rightRowContainerCtrl?: GridRowContainer }) => void
    ): void;
}

/** The members of ag-Grid 29's RowRenderer that own its draw lock. `GridApi` keeps it in `rowRenderer`. */
interface GridRowRenderer {
    redraw(...args: unknown[]): void;
    redrawAfterModelUpdate(...args: unknown[]): void;
    isRefreshInProgress(): boolean;
    releaseLockOnRefresh(): void;
    vfRedrawGuard?: true;
}

/**
 * Keeps one failed redraw from stopping a grid drawing rows for the rest of its life, and removes the throw that did it
 * on the Clients grids.
 *
 * ag-Grid 29.3.5 draws rows under a lock. `RowRenderer.redrawAfterModelUpdate` takes it with no try/finally, so if
 * anything throws while it is held, it is never released. From then on every model update and every scroll throws
 * "cannot get grid to draw rows when it is in the middle of drawing rows" and draws nothing: search, sort, expand and
 * rows scrolled into view all stop reaching the screen. Only a new grid instance clears it. Two guards:
 *
 * 1. Pinned sections never build a row that is leaving. With `animateRows`, a row that leaves the display (filtered
 *    out, collapsed, removed) keeps its row controller for 400 ms so it can fade out, and its node's `rowIndex` is
 *    already null. If the pinned-left (or right) section appears inside that window, ag-Grid 29 builds a row there for
 *    every controller, the leaving one included, and `RowNode.getRowIndexString()` throws on the null index. The
 *    column rail pins the identity block whenever the grid overflows, so this happens. Measured 2026-09-30 on every
 *    load of the Clients People grid: the saved filter restored by `restoreColumnState` hides a row, the rail pins
 *    ~120 ms later (the page-load TypeError), and `expandAll()` redraws ~360 ms later, which throws again inside the
 *    lock. This skips a row that is not displayed unless it is already drawn in that section, where it keeps its
 *    element and still fades out. It is the check ag-Grid added to `RowContainerComp.setRowCtrls` in 30.0.x (present
 *    in 30.0.6, missing in 29.3.5 and 30.0.0).
 * 2. A redraw that throws releases the lock. The error is rethrown unchanged, so it is still reported, and the next
 *    redraw works. `redraw` is only called by the two methods that have just taken the lock (`redrawAfterModelUpdate`,
 *    `redrawAfterScroll`), so a throw there always releases it. `redrawAfterModelUpdate` releases only a lock it took
 *    itself: a nested call that finds the lock held throws without touching the outer redraw's lock.
 *
 * Call it from `(gridReady)`. The column rail only gets the grid api there, so nothing pins earlier.
 *
 * @returns false, changing nothing, when the grid is already guarded or the internals are not where ag-Grid 29 keeps
 * them (a later ag-Grid).
 */
export function guardGridRedraw(api: GridApi | null | undefined): boolean {
    const internals = api as unknown as { rowRenderer?: GridRowRenderer; ctrlsService?: GridCtrls } | null | undefined;
    const rowRenderer = internals?.rowRenderer;
    const ctrls = internals?.ctrlsService;
    if (
        !rowRenderer ||
        !ctrls ||
        rowRenderer.vfRedrawGuard ||
        typeof rowRenderer.redraw !== 'function' ||
        typeof rowRenderer.redrawAfterModelUpdate !== 'function' ||
        typeof rowRenderer.isRefreshInProgress !== 'function' ||
        typeof rowRenderer.releaseLockOnRefresh !== 'function' ||
        typeof ctrls.whenReady !== 'function'
    ) {
        return false;
    }

    ctrls.whenReady(({ leftRowContainerCtrl, rightRowContainerCtrl }) => {
        skipLeavingRows(leftRowContainerCtrl, (rowCtrl) => !!rowCtrl.leftGui);
        skipLeavingRows(rightRowContainerCtrl, (rowCtrl) => !!rowCtrl.rightGui);
    });

    const redrawAfterModelUpdate = rowRenderer.redrawAfterModelUpdate.bind(rowRenderer);
    rowRenderer.redrawAfterModelUpdate = (...args: unknown[]): void => {
        const lockHeldOnEntry = rowRenderer.isRefreshInProgress();
        try {
            redrawAfterModelUpdate(...args);
        } catch (error) {
            if (!lockHeldOnEntry) {
                rowRenderer.releaseLockOnRefresh();
            }
            throw error;
        }
    };
    const redraw = rowRenderer.redraw.bind(rowRenderer);
    rowRenderer.redraw = (...args: unknown[]): void => {
        try {
            redraw(...args);
        } catch (error) {
            rowRenderer.releaseLockOnRefresh();
            throw error;
        }
    };
    rowRenderer.vfRedrawGuard = true;
    return true;
}

/** Drops from a pinned section's row list the rows that are no longer displayed and are not already drawn there. */
function skipLeavingRows(container: GridRowContainer | undefined, drawnHere: (rowCtrl: GridRowCtrl) => boolean): void {
    if (!container || typeof container.getRowCtrls !== 'function') {
        return;
    }
    const getRowCtrls = container.getRowCtrls.bind(container);
    container.getRowCtrls = (): GridRowCtrl[] =>
        getRowCtrls().filter((rowCtrl) => rowCtrl.getRowNode().displayed || drawnHere(rowCtrl));
}
